import { useEffect, useState } from "react";

import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";

import RouterLink from "../../../../../../common/components/RouterLink";
import mqttPublish from "../../../../../../common/helpers/mqttPublish";
import { useHasRoleAccess } from "../../../../../../common/hooks/useCheckRoleAccess";
import { useGetNonAdminAccess } from "../../../../../../common/hooks/useGetNonAdminAccess";
import { RouteEnum } from "../../../../../../common/models/enums";

interface DeviceImageListItemBarTitleContentProps {
  deviceId: string;
  deviceName: string;
  locationName: string;
  imageDate?: string;
  nodeId: string;
  isOnline: boolean;
  hasPanTilt: boolean;
  onLiveViewClick: () => void;
  recentFrame: string;
}

const DeviceImageListItemBarTitleContent = ({
  deviceId,
  deviceName,
  locationName,
  imageDate,
  nodeId,
  isOnline,
  hasPanTilt,
  onLiveViewClick,
  recentFrame,
}: DeviceImageListItemBarTitleContentProps) => {
  const { requestDeviceImage } = mqttPublish();

  const { nonAdminView } = useGetNonAdminAccess();

  const isDeniedAccess = useHasRoleAccess("readonly");

  // State for the text color based on imageDate recency
  const [textColor, setTextColor] = useState<string>("inherit");
  // This parses the timestamp in the format of "3:07 PM Sep 4, 2025"
  // It is meant to be used with the formatTimestamp function in timestampUtils.ts
  const isImageDateRecent = (dateString?: string): boolean => {
    if (!dateString) return true;

    const re =
      /^(\d{1,2}):(\d{2})\s*(AM|PM)\s+([A-Za-z]{3,4})\s+(\d{1,2}),\s*(\d{4})$/;

    const m = dateString.trim().match(re);

    if (!m) {
      console.warn("Unexpected date format:", dateString);

      return true;
    }

    const [, h, min, ampm, monStr, dayStr, yearStr] = m;

    const monthIndex: Record<string, number> = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11,
    };
    const month = monthIndex[monStr.toLowerCase()];

    if (month == null) {
      console.warn("Unknown month:", monStr);

      return true;
    }

    let hour = parseInt(h, 10) % 12; // 12 -> 0

    if (ampm.toUpperCase() === "PM") hour += 12;

    const minute = parseInt(min, 10);
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);

    // Construct as *local* time
    const imageDate = new Date(year, month, day, hour, minute, 0, 0);

    if (isNaN(imageDate.getTime())) {
      console.warn("Invalid constructed date from:", dateString);

      return true;
    }

    const fifteenMinutesInMs = 15 * 60 * 1000;

    return Math.abs(Date.now() - imageDate.getTime()) <= fifteenMinutesInMs;
  };

  // Update text color when imageDate changes
  useEffect(() => {
    const color = isImageDateRecent(imageDate) ? "inherit" : "red";

    setTextColor(color);
  }, [imageDate]);

  const handleGetNewImage = (): void => {
    requestDeviceImage({
      deviceId,
      nodeId,
      s3Key: recentFrame,
      footer: "TRUE",
      successMessage: "Image update request sent successfully!",
      errorMessage: "Failed to send image update request",
    });
  };

  return (
    <Box sx={{ padding: "0.5em" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          margin: "0.5em 0",
        }}
      >
        <Typography
          variant="body1Regular"
          sx={{
            "&:hover": {
              cursor: "pointer",
              color: (theme): string => theme.palette.primary.main,
            },
          }}
          onClick={onLiveViewClick}
        >
          {deviceName}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: hasPanTilt ? "baseline" : "center",
          gap: "0.5em",
        }}
      >
        <Typography variant="inputLabel" component="span">
          Location
        </Typography>
        <Typography
          variant="body2Regular"
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {locationName}
        </Typography>
        {hasPanTilt && !isDeniedAccess && (
          <Button
            variant="text"
            sx={{
              flexShrink: 0,
              marginLeft: "auto",
              padding: "2px 4px",
              height: "26px",
            }}
          >
            <RouterLink
              title="Zones List"
              to={
                nonAdminView
                  ? `${
                      RouteEnum.SensoryLiveViewZoneListShort
                    }/${encodeURIComponent(deviceId)}`
                  : `${RouteEnum.ZoneListShort}/${encodeURIComponent(deviceId)}`
              }
              state={{
                deviceId,
              }}
            />
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
          <Typography variant="inputLabel" component="span">
            Last image
          </Typography>

          <Typography
            variant="body2Regular"
            component="span"
            sx={{
              fontSize: "0.75em",
              color: textColor,
            }}
          >
            {imageDate || "-"}
          </Typography>
        </Box>

        <Tooltip
          title={isOnline ? "Update last image" : "Device is offline"}
          placement="top"
        >
          <span>
            <IconButton
              size="small"
              onClick={handleGetNewImage}
              disabled={!isOnline}
            >
              <RefreshIcon
                fontSize="small"
                sx={{ color: isOnline ? "primary.main" : "disabled.main" }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DeviceImageListItemBarTitleContent;
